import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../services/api.js';
import { TOGGLEABLE_KINDS, KIND_PREF_LABELS, STAFF_ONLY_KINDS, type ToggleableKind } from '../notifications/display.js';
import { isKnownCommunicoStaff } from '../utils/sessionUser.js';
import './cos-button.js';
import '../design-system/cos-modal.js';
import '../design-system/cos-checkbox.js';

/**
 * <cos-notification-preferences> — Stage 75. A modal with one checkbox per
 * user-toggleable source kind. All ON by default = the kind is NOT in
 * muted_kinds; toggling a box OFF adds its kind. Each toggle auto-saves via
 * PATCH /me/preferences/notifications. Esc / cancel / outside-click close.
 *
 * Built on cos-modal. Every change here already auto-saves, so there is no
 * "Cancel and discard" semantics to offer — the footer SLOT carries a single
 * "Done" action rather than the standard Cancel/Primary pair (the
 * documented rare case the standard footer doesn't fit).
 */
@customElement('cos-notification-preferences')
export class CosNotificationPreferences extends LitElement {
	// Source of truth: the set of MUTED kinds. A checkbox is checked when its
	// kind is NOT muted.
	@state() private _muted = new Set<string>();
	@state() private _loaded = false;

	static styles = css`
		:host {
			display: contents;
		}
		.hint {
			margin: 0 0 var(--cos-space-2);
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
		}
		.opt {
			display: flex;
			align-items: center;
			padding: 10px 4px;
			font-size: var(--cos-font-size-base);
			color: var(--cos-text-primary);
			border-bottom: 1px solid var(--cos-surface-raised);
		}
		.opt:last-of-type { border-bottom: none; }
		.opt cos-checkbox {
			width: 100%;
		}
	`;

	connectedCallback(): void {
		super.connectedCallback();
		void this._load();
	}

	private async _load() {
		try {
			const r = (await api.getNotificationPreferences()) as { muted_kinds?: string[] };
			this._muted = new Set(r?.muted_kinds || []);
		} catch {
			this._muted = new Set();
		} finally {
			this._loaded = true;
		}
	}

	private async _toggle(kind: ToggleableKind, enabled: boolean) {
		const next = new Set(this._muted);
		if (enabled) next.delete(kind);
		else next.add(kind);
		this._muted = next;
		try {
			await api.updateNotificationPreferences([...next]);
		} catch {
			// Optimistic — server is authoritative on next open.
		}
	}

	private _close(): void {
		this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
	}

	render() {
		return html`
			<cos-modal open heading="Notification settings" size="sm" @cos:close=${() => this._close()}>
				<p class="hint">Choose which notifications you want to receive.</p>
				${TOGGLEABLE_KINDS.filter((kind) => isKnownCommunicoStaff() || !STAFF_ONLY_KINDS.includes(kind)).map((kind) => {
					const enabled = !this._muted.has(kind);
					return html`
						<div class="opt">
							<cos-checkbox
								.checked=${enabled}
								?disabled=${!this._loaded}
								label=${KIND_PREF_LABELS[kind]}
								@cos:change=${(e: CustomEvent<{ checked: boolean }>) => this._toggle(kind, e.detail.checked)}
							></cos-checkbox>
						</div>
					`;
				})}
				<div slot="footer">
					<cos-button size="sm" variant="secondary" @click=${() => this._close()}>Done</cos-button>
				</div>
			</cos-modal>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-notification-preferences': CosNotificationPreferences;
	}
}
